/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: video-metadata-component.ts
 *
 * Created by randysorensen on 9/12/18.
 */

function PackageMediaMainVideoAssetSelectorController (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    AssetValidationService: JS5Object,
    DynamicPropertiesService: JS5Object,
    ModalService: JS5Object,
    PackageFileUploadService: JS5Object,
    PackageMediaService: JS5Object,
    PackageUtilService: JS5Object,
    TempFileUploadService: JS5Object,
) {
    this.getMainVideo = (): JS5Object => {
        return PackageMediaService.getMainVideo();
    };

    this.getPrimaryAcceptTemplateMember = (): JS5Object => {
        return PackageMediaService.getPrimaryAcceptTemplateMember();
    };

    const setMainVideo = (virtualPath: string, isRemoteUploadEnabled: boolean) => {
        let mainVideo = this.getMainVideo();
        _.merge(mainVideo, {
            acceptTemplateMember: PackageMediaService.packageMediaData.selectedVideoAcceptTemplateMember,
            assetId: -1,
            pendingIngest: isRemoteUploadEnabled,
            virtualPath: virtualPath
        });

        PackageUtilService.editorForm.$setDirty();
    };


    //================//
    // Template Logic //
    //================//

    this.remoteUploadEnabled = () => {
        return DynamicPropertiesService.getPropValueAsBoolean('mediaTabRemoteUpload.enabled', false);
    }

    this.onViewAssetButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openViewAssetModal(this.getMainVideo());
    };

    this.onQualityControlButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openQualityControlModal(this.getMainVideo());
    };

    const hasMainVideo = () => {
        return !!_.get(this.getMainVideo(), `virtualPath`, null);
    };

    const resetPackageMediaForVideoChange = () => {
        let selectedVideoAcceptTemplateMember = this.getPrimaryAcceptTemplateMember();
        PackageMediaService.resetAllMedia();
        this.currentFile = null;
        this.prevMainVideo = PackageMediaService.getMainVideo();
        PackageMediaService.packageMediaData.selectedVideoAcceptTemplateMember = selectedVideoAcceptTemplateMember;
        PackageMediaService.packageObject.primaryAcceptTemplateMember = selectedVideoAcceptTemplateMember;
    };

    this.onCurrentFileChanged = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
        setMainVideo(null, false);
        if (_.get(this, 'currentFile.status') === 'PASSED') {
            switch (this.currentFile.type) {
                case 'ASPERA':
                    // TODO: Test in UI when Aspera is configured
                    PackageMediaService.setMainVideoMediaInfoXML(this.currentFile.results[0].mediaInfo);
                    setMainVideo(this.currentFile.results[0].uploadedData, true);
                    break;
                case 'LOCAL':
                    // Display fileName (Correct virtualPath is set when media is saved)
                    PackageFileUploadService.registerLocalConfigForUpload(this.uploaderConfig);
                    PackageMediaService.setMainVideoMediaInfoXML(this.currentFile.results[0].mediaInfo);
                    setMainVideo(`s3:fakePath/${this.currentFile.files[0].file.name}`, false);
                    break;
                case 'REMOTE':
                    setMainVideo(this.currentFile.files[0].virtualPath, false);
                    break;
            }
        }
    };


    //========================//
    // Remote File Validation //
    //========================//

    const openChangeVideoConfirmationModal = () => {
        let deferred = $q.defer();
        ModalService.showMessageModal(
            {
                messageText:
                    `Are you sure you want to change the package's main video? This will invalidate all of your ` +
                    ` assets, and reintialize the view.`,
                pullCancelLeft: true,
                title: 'Change Package Main Video',
                windowClass: 'confirm-modal-lg',
                showOK: true
            },
            () => {
                resetPackageMediaForVideoChange();
                deferred.resolve(true);
            },
            () => {
                deferred.resolve(false);
            });
        return deferred.promise;
    };

    this.promptFileWarnings = () => {
        if (!hasMainVideo()) {
            return true;
        } else {
            return openChangeVideoConfirmationModal();
        }
    };

    this.validateRemoteFile = (fileSystemItem: JS5Object) => {
        const acceptProfileId = this.getPrimaryAcceptTemplateMember().acceptProfile.id;
        const virtualPath = fileSystemItem.parent + fileSystemItem.name;
        return AssetValidationService.validateMediaRemote(virtualPath, acceptProfileId);
    };


    //========================//
    // Aspera File Validation //
    //========================//

    this.validateAsperaFile = (asperaFileObject: JS5Object) => {
        return AssetValidationService.validateMediaLocal(asperaFileObject.file, this.getPrimaryAcceptTemplateMember().acceptProfile)
            .then((result: JS5Object) => {
                return PackageFileUploadService.parseAsperaValidationResult(PackageMediaService.packageObject, asperaFileObject, result);
            });
    };


    //=======================//
    // Local File Validation //
    //=======================//

    this.getLocalFilePreValidationErrors = (file: File, filter: JS5Object) => {
        // Doesn't add any local file filters
    };

    this.validateLocalFile = (fileItem: JS5Object, uploaderConfig: JS5Object) => {
        fileItem.onVirtualPathSet = (virtualPath: string) => {
            setMainVideo(virtualPath, true);
        };
        return AssetValidationService.validateMediaLocal(fileItem._file, this.getPrimaryAcceptTemplateMember().acceptProfile);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.prevMainVideo = PackageMediaService.getMainVideo();
    };

    this.initializeLocalUploaderConfig = (uploaderConfig: JS5Object) => {
        TempFileUploadService.initialize(uploaderConfig);
        this.uploaderConfig = uploaderConfig;
    };

    this.$doCheck = () => {
        let mainVideo = PackageMediaService.getMainVideo();
        if (this.uploaderConfig && this.prevMainVideo !== mainVideo) {
            this.currentFile = null;
            this.prevMainVideo = mainVideo;
        }
    };

    this.$onDestroy = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
    };
}

angular.module('prism-ui.package').component(
    'packageMediaVideoMainAssetSelector',
    {
        controller: PackageMediaMainVideoAssetSelectorController,
        templateUrl: 'package/editor/components/view/media/assetSelector/videoMain/package-media-video-main-asset-selector-template.html',
        bindings: {
        }
    });
